import { Component, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Ticket } from 'src/app/models/core/ticket.model';
import { VentaRapidaCloneContext } from 'src/app/models/venta-rapida/venta-rapida-clone-context.model';
import { VentaRapidaAssignRequest, VentaRapidaAssignedTicket } from 'src/app/models/venta-rapida/venta-rapida-assign.model';
import { VentaRapidaAssignService } from 'src/app/services/venta-rapida/venta-rapida-assign.service';
import { Product } from 'src/app/models/core/product.model';
import { Event } from 'src/app/models/core/event.model';
import { Seller } from 'src/app/models/core/seller.model';

@Component({
  selector: 'app-confirm-venta-rapida-clone-modal',
  templateUrl: './confirm-venta-rapida-clone-modal.component.html',
  styleUrls: ['./confirm-venta-rapida-clone-modal.component.scss'],
  standalone: false
})
export class ConfirmVentaRapidaCloneModalComponent {
  @Input() ticket!: Ticket;
  @Input() ventaRapidaContext!: VentaRapidaCloneContext;

  isCloning = false;

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly assignService: VentaRapidaAssignService,
  ) {}

  get assigneeLabel(): string {
    const { user, anonymousIntent } = this.ventaRapidaContext;
    if (user) {
      return user.fullname + (user.dni ? ` · DNI ${user.dni}` : '');
    }
    if (!anonymousIntent || anonymousIntent.kind === 'full-anonymous') return 'Anónimo';
    if (anonymousIntent.kind === 'name') return anonymousIntent.value;
    if (anonymousIntent.kind === 'dni') return `DNI ${anonymousIntent.value}`;
    return 'Anónimo';
  }

  get formattedAmount(): string {
    return `$${this.ventaRapidaContext.amount.toLocaleString('es-AR')}`;
  }

  closeModal(): void {
    this.modalCtrl.dismiss();
  }

  async cloneTicket(): Promise<void> {
    if (this.isCloning) return;
    this.isCloning = true;

    const { eventId, amount, user, anonymousIntent, product } = this.ventaRapidaContext;

    const base: VentaRapidaAssignRequest = {
      event_id: Number(eventId),
      product_id: product.id,
      amount,
      quantity: 1,
      source: 'TQR-mobile',
    };

    let request: VentaRapidaAssignRequest;
    if (user) {
      request = { ...base, user_id: user.id };
    } else if (anonymousIntent?.kind === 'name') {
      request = { ...base, fullname: anonymousIntent.value };
    } else if (anonymousIntent?.kind === 'dni') {
      request = { ...base, dni: anonymousIntent.value };
    } else {
      request = base;
    }

    this.assignService.assign(request).subscribe({
      next: async (response) => {
        const assigned = response.tickets?.[0];
        if (assigned) {
          const newTicket = this.mapToTicket(assigned, product);
          await this.modalCtrl.dismiss(
            { newTicket, assistantUuid: assigned.assistant_uuid },
            'cloned',
          );
        } else {
          this.isCloning = false;
        }
      },
      error: () => {
        this.isCloning = false;
      },
    });
  }

  private mapToTicket(assigned: VentaRapidaAssignedTicket, product: Product): Ticket {
    return {
      id: assigned.ticket_id,
      uuid: assigned.uuid,
      reference: '',
      status: '',
      payment_status: false,
      payment_method: '',
      product,
      seller: {} as Seller,
      event: product.event ?? ({} as Event),
      assistants_count: 0,
      quota: assigned.quantity,
    };
  }
}
